import type { Metadata } from 'next';
import { getSiteUrl } from '@/lib/site';
import type { PublicLocale } from '@/lib/locale';

type LinkItem = {
  id: string;
  label: string;
  href: string;
};

const siteUrl = getSiteUrl();

export const publicCopy = {
  en: {
    metadata: {
      siteTitle: 'Academy of Squash | Presence Over Performance',
      siteDescription:
        'Presence-first squash coaching in the Randstad from a PSA World Tour competitor with 15+ years of experience. Private coaching, juniors, camps, corporate events, and summer programmes.',
      ogDescription:
        'Elite squash coaching rooted in awareness and mastery — from a PSA World Tour competitor with 15+ years of experience.',
      twitterDescription:
        'Presence-first squash coaching in the Randstad. Book a session or get in touch.',
    },
    header: {
      academyName: 'Academy of Squash',
      ariaMainNavigation: 'Main navigation',
      moreLabel: 'More',
      languageSwitchLabel: 'Nederlands',
      bookNow: 'Book now',
      openMenu: 'Open menu',
      closeMenu: 'Close menu',
      primaryLinks: [
        { id: 'nav_about', label: 'About', href: '/#about' },
        { id: 'nav_programmes', label: 'Programmes', href: '/#services' },
        { id: 'nav_corporate', label: 'Corporate', href: '/corporate' },
        { id: 'nav_coach', label: 'Coach', href: '/coach' },
        { id: 'nav_contact', label: 'Contact', href: '/#contact' },
      ] satisfies LinkItem[],
      secondaryLinks: [
        { id: 'nav_sponsors', label: 'Partners', href: '/#sponsors' },
        { id: 'nav_philosophy', label: 'Philosophy', href: '/#philosophy' },
        { id: 'nav_camps', label: 'Summer Camps', href: '/#camps' },
        { id: 'nav_reviews', label: 'Reviews', href: '/#testimonials' },
        { id: 'nav_blog', label: 'Blog', href: '/blog' },
      ] satisfies LinkItem[],
      homeLabel: 'Home',
    },
    footer: {
      quickLinks: 'Quick links',
      more: 'More',
      followUs: 'Follow us',
      location: 'Randstad area, Netherlands',
      bookSession: 'Book a session',
      rightsReserved: 'All rights reserved.',
      privacyPolicy: 'Privacy policy',
      termsOfService: 'Terms of service',
      footerTagline: 'Come find your story on court.',
      links: [
        { id: 'footer_home', label: 'Home', href: '/#hero' },
        { id: 'footer_sponsors', label: 'Partners', href: '/#sponsors' },
        { id: 'footer_philosophy', label: 'Philosophy', href: '/#philosophy' },
        { id: 'footer_about', label: 'About', href: '/#about' },
        { id: 'footer_services', label: 'Programmes', href: '/#services' },
        { id: 'footer_corporate', label: 'Corporate', href: '/corporate' },
        { id: 'footer_camps', label: 'Camps', href: '/#camps' },
        { id: 'footer_testimonials', label: 'Testimonials', href: '/#testimonials' },
        { id: 'footer_contact', label: 'Contact', href: '/#contact' },
      ] satisfies LinkItem[],
    },
    cookie: {
      title: 'Cookies & privacy',
      body: 'We use cookies to run the site and, if you agree, to measure traffic with analytics. See our',
      privacyPolicy: 'privacy policy',
      essentialOnly: 'Essential only',
      acceptAnalytics: 'Accept analytics',
    },
    mobileBookingBar: {
      cta: 'Book now',
    },
    notFound: {
      title: 'Page Not Found',
      body: "The page you're looking for doesn't exist. Let's get you back!",
      goBack: 'Go Back',
      backHome: 'Back to Home',
    },
    home: {
      hero: {
        eyebrow: 'Academy of Squash',
        title: 'Presence Over Performance',
        subtitle:
          'Elite squash coaching rooted in awareness, intention, and mastery — from a PSA World Tour competitor with 15+ years of experience.',
        primaryCta: 'Book a session',
        secondaryCta: 'Explore our programmes',
        imageAlt: 'Squash player driving through a forehand during match play',
      },
      sponsors: {
        eyebrow: 'Trusted partners',
        title: 'The team behind the team',
        description:
          'Equipment and collaborations that help players train and compete with confidence.',
        fallbackPartnerRole: 'Partner organisation',
        squashpointRole: 'Official equipment partner',
        missingPartnerNotice:
          'Add NEXT_PUBLIC_PORTWISE_URL in your environment to show an additional partner card.',
      },
      philosophy: {
        eyebrow: 'Presence Over Performance',
        title: 'This Is a',
        titleItalic: 'Competitive Edge',
        p1: "Most players are stuck in the same trap: they play the game, but they're not really in the game. They're replaying the last rally, worrying about the next point, and that mental noise costs more points than technique ever will.",
        p2: "Players who know themselves and can access their emotional model under pressure play with a freedom that is very hard to face. They don't fight who they are on court. They lean into it.",
        p3: 'When you are truly in the moment, results follow naturally. We give you the tools to get there and stay there.',
        cta: 'See how we train presence',
        imageAlt: 'Player in an intense rally while staying composed on court',
      },
      about: {
        eyebrow: 'About Blessing & The Academy',
        yearsExperience: 'Years Experience',
        title: 'Born from a Life on the',
        titleItalic: 'Move.',
        p1: 'I grew up in Zimbabwe with a dream to one day compete on the PSA in Europe. That dream took me from home to university in South Africa, then eventually to the Netherlands.',
        p2: 'Along the way I played and coached across different countries and cultures. Every opponent, teammate, and player I met brought a story that changed how I understand the game.',
        p3: 'What I discovered is that squash is never just about squash. It is about the person holding the racket. The Academy of Squash was built on that insight.',
        playersCoached: 'Players Coached',
        continentsPlayed: 'Continents Played',
        cta: 'Train with Blessing — book now',
        imageAlt: 'Head coach holding a squash racket in a portrait at the court',
      },
      whoWeWorkWith: {
        eyebrow: 'Who We Work With',
        title: 'Every Level. Every Story.',
        titleItalic: 'One Approach.',
        p1: "Whether you've never held a racket, are competing nationally, are a junior chasing a first ranking, or a corporate team looking for a real shared challenge, we always begin with the same question:",
        highlight: 'Who are you, and what brings you here?',
        p2: "When you walk onto court with us, you don't need to perform a version of yourself you think we want to see. You just need to show up and we take it from there.",
        cta: 'Find the right programme',
        imageAlt: 'Group of Academy of Squash players after a training session',
      },
      holistic: {
        eyebrow: 'The Full Picture',
        title: 'Better Player.',
        titleItalic: 'More Grounded Person.',
        p1: 'We take physical and mental performance seriously. Our programmes are built with strength and conditioning coaches, physios, and mental performance specialists.',
        p2: 'We run tactical analysis, video review, and tournament preparation. Every performance tool is on the table, but it all sits inside a bigger frame.',
        p3: 'The most important thing we build is a player who is more self-aware, more resilient, and more fully themselves on court than when they walked in.',
        cta: 'Book a consultation',
        imageAlt: 'Coach observing a session and supporting whole-player development',
      },
      services: {
        eyebrow: 'Our Programmes',
        title: 'Every Service Starts with',
        titleItalic: 'Your Story',
        intro:
          'We help you find the emotional state that unlocks your best squash, then train you to reach it consistently.',
        cards: [
          {
            id: 'service_private',
            icon: 'UserIcon',
            title: 'Private Coaching',
            description:
              'One-on-one sessions built around your story. We refine technique, develop strategy, and build the mental habits that support how you naturally play.',
            ctaLabel: 'Book a session',
          },
          {
            id: 'service_group',
            icon: 'UserGroupIcon',
            title: 'Group Training & Clinics',
            description:
              'High-energy sessions where you explore how to express yourself under pressure through drills, match play, and collaborative learning.',
            ctaLabel: 'Reserve your spot',
          },
          {
            id: 'service_junior',
            icon: 'AcademicCapIcon',
            title: 'Junior Development',
            description:
              'Long-term pathways for young athletes that build self-awareness, resilience, and a genuine love of growth before short-term results.',
            ctaLabel: 'Start training',
          },
          {
            id: 'service_tournament',
            icon: 'TrophyIcon',
            title: 'Tournament Preparation',
            description:
              'Competition prep that combines tactical analysis with routines that help you access your best game in high-pressure match situations.',
            ctaLabel: 'Book a trial',
          },
          {
            id: 'service_fitness',
            icon: 'BoltIcon',
            title: 'Fitness & Conditioning',
            description:
              'Strength, agility, and endurance programming developed with sports specialists so your body and mind stay present in long rallies.',
            ctaLabel: 'Enquire now',
          },
          {
            id: 'service_video',
            icon: 'VideoCameraIcon',
            title: 'Video Analysis',
            description:
              'Detailed footage review that reveals hidden movement, decision, and emotional patterns so you can make targeted adjustments.',
            ctaLabel: 'Book analysis',
          },
          {
            id: 'service_corporate',
            icon: 'BriefcaseIcon',
            title: 'Corporate Experiences',
            description:
              'Court-based sessions for leadership, communication, and connection, translating performance insights directly into the workplace.',
            ctaLabel: 'Plan an event',
          },
        ],
      },
      pricing: {
        eyebrow: 'Pricing',
        title: 'Clear',
        titleItalic: 'starting points',
        intro:
          'Indicative figures help you compare options — final rates depend on venue, frequency, and programme. Book online or enquire for a tailored quote.',
        fromPrefix: 'From',
        privateFallback: 'Ask for current packages',
        groupFallback: 'Seasonal blocks — enquire for dates',
        rows: [
          {
            title: 'Private coaching',
            note: 'Sessions tailored to your game; bundles available.',
            cta: 'Reserve your spot',
          },
          {
            title: 'Group clinics & training',
            note: 'High-energy blocks with clear progression.',
            cta: 'Start training',
          },
          {
            title: 'Junior development',
            note: 'Long-term focus on awareness and love of the game.',
            cta: 'See availability',
            hint: 'Pathways by age and ambition',
          },
        ],
      },
      camps: {
        eyebrow: 'Summer Camps',
        title: 'Train Abroad.',
        titleItalic: 'Compete Everywhere.',
        intro:
          'Each summer, we combine elite coaching with cultural immersion for a developmental experience that goes far beyond a normal training week. Equipment for select programmes is supported by our partner',
        squashpoint: 'Squashpoint',
        announcement:
          'Summer 2026 dates are subject to confirmation — register your interest below to be notified first.',
        registerInterest: 'Register Interest',
        registerAriaPrefix: 'Register interest for',
        registerAriaSuffix: 'summer camp',
        callout:
          'Our summer camps are intentionally selective so each player receives focused coaching attention. Early enquiries are recommended.',
        cards: [
          {
            id: 'camp_train_abroad',
            title: 'Train Abroad',
            subtitle: 'Elite Coaching Blocks',
            description:
              'Each summer, we take the Academy on the road and train in high-level environments that challenge players to adapt quickly.',
            image: '/assets/images/aos-camp-train.jpg',
            imageAlt: 'Squash player training at pace during an international camp block',
          },
          {
            id: 'camp_compete_everywhere',
            title: 'Compete Everywhere',
            subtitle: 'Cross-Cultural Match Experience',
            description:
              'Players meet athletes from different countries, compete in fresh contexts, and discover new dimensions of their own game.',
            image: '/assets/images/aos-camp-compete.jpg',
            imageAlt: 'Group of players gathered at a summer camp training day',
          },
          {
            id: 'camp_transformative_growth',
            title: 'Transformative Growth',
            subtitle: 'Beyond Holiday Camps',
            description:
              'These camps are designed to push technical, mental, and personal growth so players return home more complete competitors.',
            image: '/assets/images/aos-camp-grow.jpg',
            imageAlt: 'Coach reviewing movement with players during an immersive camp session',
          },
        ],
      },
      testimonials: {
        eyebrow: 'Player voices',
        title: "Stories we're",
        titleItalic: 'proud to be part of',
        p1: "We see meaningful growth on court every week. Detailed testimonials are shared on request, and we're collecting permission to publish named quotes here soon.",
        p2: "Prefer a reference from a player or organisation you can relate to? Ask when you book or enquire — we'll connect you where appropriate.",
        primaryCta: 'Book a session',
        secondaryCta: 'Enquire first',
      },
      contact: {
        eyebrow: 'Ready to step on court?',
        titleBefore: 'Come find your',
        titleItalic: 'story.',
        p1: 'Squash is a small box. Four walls, a ball, and nowhere to hide. Inside that box there is enormous space to express who you are.',
        p2: 'Whatever brings you to court — joy, ambition, competition, or simply the desire to get better — we are here for you.',
        validEmail: 'Please enter a valid email address.',
        submitErrorFallback: 'Something went wrong while sending your message.',
        networkError: 'Network error. Please try again in a moment.',
        success:
          "Thank you. Your request has been sent and received. We'll respond within 24 hours.",
        fullName: 'Full Name *',
        fullNamePlaceholder: 'John Doe',
        emailAddress: 'Email Address *',
        emailPlaceholder: 'john@example.com',
        phoneOptional: 'Phone (Optional)',
        serviceInterest: 'Service Interest *',
        serviceAria: 'Select the service you are interested in',
        message: 'Message',
        messagePlaceholder: 'Tell us about your goals, level, and what kind of support you want.',
        privacyAgreementPrefix: 'By submitting this form, you agree to our',
        privacyPolicy: 'privacy policy',
        privacyAgreementSuffix: "We'll respond within 24 hours.",
        sending: 'Sending...',
        sendMessage: 'Send Message',
        infoEmail: 'Email',
        infoPhone: 'Phone',
        infoLocation: 'Location',
        location: 'Randstad area, Netherlands',
        services: [
          'Private Coaching',
          'Group Training & Clinics',
          'Junior Development',
          'Tournament Preparation',
          'Fitness & Conditioning',
          'Video Analysis',
          'Summer Camps',
          'Corporate Experiences',
          'Other',
        ],
      },
    },
    pages: {
      corporate: {
        metadataTitle: 'Corporate squash events',
        metadataDescription:
          'Team building and leadership development through squash in the Randstad — tailored corporate sessions with Academy of Squash.',
        backHome: '← Back to home',
        title: 'Corporate',
        titleItalic: 'squash events',
        intro:
          'Bring your team onto the court for sessions that blend communication, pressure, and reflection. We design formats for leadership offsites, department days, and client hospitality — from introductory tasters to multi-hour programmes with facilitated debriefs.',
        bullets: [
          'Indoor squash venues across the Randstad (venues confirmed per booking)',
          'Indoor/outdoor warm-ups and team challenges where facilities allow',
          'Optional link to workplace themes: feedback, decision-making under stress, trust',
        ],
        primaryCta: 'Book or enquire',
        secondaryCta: 'Contact form',
      },
      coach: {
        metadataTitle: 'Coach & PSA credentials',
        metadataDescription:
          'PSA World Tour competitor with 15+ years of coaching experience — the story behind Academy of Squash and the Presence Over Performance philosophy.',
        backHome: '← Back to home',
        title: 'Coach',
        titleItalic: 'bio',
        p1: 'Blessing built Academy of Squash on a simple idea: squash reveals who you are under pressure. As a competitor on the PSA World Tour and a coach across continents, the work is about presence — the ability to stay in the moment, use your emotional model, and play freely when it counts.',
        p2: 'With 15+ years of coaching experience and deep exposure to international competition, sessions combine technical clarity with mental skills that transfer beyond the court.',
        p3: 'Philosophy: Presence Over Performance — performance is the by-product of how present, intentional, and self-aware you can become in the heat of play.',
        cta: 'Train with Blessing — book now',
      },
      blog: {
        metadataTitle: 'Blog & insights',
        metadataDescription:
          'Squash training insights, movement ideas, and Academy updates — coming soon.',
        backHome: '← Back to home',
        title: 'Insights',
        titleItalic: 'coming soon',
        body: "We're preparing articles and videos on movement, mindset, and match strategy. Until then, follow us on social or get in touch for coaching updates.",
        cta: 'Get notified',
      },
      privacy: {
        metadataTitle: 'Privacy policy',
        metadataDescription:
          'How Academy of Squash handles personal data, cookies, and communications for visitors and clients in the Netherlands.',
        backHome: '← Back to home',
        title: 'Privacy policy',
        updated: 'Last updated: 8 April 2026',
        paragraphs: [
          'Academy of Squash (“we”, “us”) respects your privacy. This policy explains what we collect when you use academyofsquash.com, how we use it, and your rights under the EU General Data Protection Regulation (GDPR).',
        ],
        sections: [
          {
            title: 'Who we are',
            body: 'Contact: info@academyofsquash.com\nPhone: +31 6 5896 9744\nLocation: Randstad area, Netherlands',
          },
          {
            title: 'Data we collect',
            list: [
              'Contact form: name, email, phone (optional), service interest, and your message — to respond to enquiries and arrange coaching.',
              'Technical data: standard server and analytics information (e.g. page views) if you accept analytics cookies.',
            ],
          },
          {
            title: 'Legal bases',
            body: 'We process enquiry data on the basis of contract or legitimate interest in responding to requests. Analytics runs only with your consent, managed through our cookie banner.',
          },
          {
            title: 'Retention',
            body: 'We keep enquiry records only as long as needed to handle your request and any follow-up coaching relationship, unless a longer period is required by law.',
          },
          {
            title: 'Your rights',
            body: 'You may request access, correction, deletion, restriction, or portability of your personal data, and object to certain processing. You may lodge a complaint with your local supervisory authority (in the Netherlands: Autoriteit Persoonsgegevens).',
          },
          {
            title: 'Cookies',
            body: 'Essential cookies are required for the site to function. Optional analytics cookies are only activated if you choose “Accept analytics” in the banner.',
          },
        ],
        disclaimer:
          'This text is a practical summary and does not replace legal advice. Adapt it with your lawyer for your exact setup (hosting, email provider, booking tool).',
      },
      terms: {
        metadataTitle: 'Terms of service',
        metadataDescription: 'Terms of use for the Academy of Squash website and related services.',
        backHome: '← Back to home',
        title: 'Terms of service',
        updated: 'Last updated: 8 April 2026',
        intro:
          'By using this website you agree to these terms. Coaching, camps, and corporate events are booked under separate agreements or confirmations sent by Academy of Squash.',
        sections: [
          {
            title: 'Use of the site',
            body: 'Content is provided for information. We may update pages, programmes, and availability without notice. External links (partners, booking tools) are provided for convenience; we are not responsible for third-party sites or services.',
          },
          {
            title: 'Liability',
            body: 'Squash is a physical sport. Participants are responsible for their own health and for following facility rules. To the extent permitted by law, we limit liability arising from use of this website; liability for personal injury or services is governed by your coaching or event agreement and applicable law.',
          },
          {
            title: 'Intellectual property',
            body: 'Text, images, and branding on this site belong to Academy of Squash or its licensors. Please do not reuse without permission.',
          },
        ],
        disclaimer: 'Have these terms reviewed by your legal adviser before publication.',
      },
    },
  },
  nl: {
    metadata: {
      siteTitle: 'Academy of Squash | Presence Over Performance',
      siteDescription:
        'Squashcoaching met focus op aanwezigheid in de Randstad door een PSA World Tour-speler met meer dan 15 jaar ervaring. Privécoaching, jeugd, kampen, corporate events en zomerprogramma’s.',
      ogDescription:
        'Topniveau squashcoaching vanuit bewustzijn en meesterschap — door een PSA World Tour-speler met meer dan 15 jaar ervaring.',
      twitterDescription:
        'Presence-first squash coaching in de Randstad. Boek een sessie of neem contact op.',
    },
    header: {
      academyName: 'Academy of Squash',
      ariaMainNavigation: 'Hoofdnavigatie',
      moreLabel: 'Meer',
      languageSwitchLabel: 'English',
      bookNow: 'Boek nu',
      openMenu: 'Menu openen',
      closeMenu: 'Menu sluiten',
      primaryLinks: [
        { id: 'nav_about', label: 'Over ons', href: '/#about' },
        { id: 'nav_programmes', label: "Programma's", href: '/#services' },
        { id: 'nav_corporate', label: 'Corporate', href: '/corporate' },
        { id: 'nav_coach', label: 'Coach', href: '/coach' },
        { id: 'nav_contact', label: 'Contact', href: '/#contact' },
      ] satisfies LinkItem[],
      secondaryLinks: [
        { id: 'nav_sponsors', label: 'Partners', href: '/#sponsors' },
        { id: 'nav_philosophy', label: 'Filosofie', href: '/#philosophy' },
        { id: 'nav_camps', label: 'Zomerkampen', href: '/#camps' },
        { id: 'nav_reviews', label: 'Ervaringen', href: '/#testimonials' },
        { id: 'nav_blog', label: 'Blog', href: '/blog' },
      ] satisfies LinkItem[],
      homeLabel: 'Home',
    },
    footer: {
      quickLinks: 'Snelle links',
      more: 'Meer',
      followUs: 'Volg ons',
      location: 'Regio Randstad, Nederland',
      bookSession: 'Boek een sessie',
      rightsReserved: 'Alle rechten voorbehouden.',
      privacyPolicy: 'Privacybeleid',
      termsOfService: 'Algemene voorwaarden',
      footerTagline: 'Vind jouw verhaal op de baan.',
      links: [
        { id: 'footer_home', label: 'Home', href: '/#hero' },
        { id: 'footer_sponsors', label: 'Partners', href: '/#sponsors' },
        { id: 'footer_philosophy', label: 'Filosofie', href: '/#philosophy' },
        { id: 'footer_about', label: 'Over ons', href: '/#about' },
        { id: 'footer_services', label: "Programma's", href: '/#services' },
        { id: 'footer_corporate', label: 'Corporate', href: '/corporate' },
        { id: 'footer_camps', label: 'Kampen', href: '/#camps' },
        { id: 'footer_testimonials', label: 'Ervaringen', href: '/#testimonials' },
        { id: 'footer_contact', label: 'Contact', href: '/#contact' },
      ] satisfies LinkItem[],
    },
    cookie: {
      title: 'Cookies & privacy',
      body: 'We gebruiken cookies om de website te laten werken en, als je akkoord gaat, om verkeer te meten met analytics. Lees ons',
      privacyPolicy: 'privacybeleid',
      essentialOnly: 'Alleen essentieel',
      acceptAnalytics: 'Analytics accepteren',
    },
    mobileBookingBar: {
      cta: 'Boek nu',
    },
    notFound: {
      title: 'Pagina niet gevonden',
      body: 'De pagina die je zoekt bestaat niet. We helpen je terug.',
      goBack: 'Ga terug',
      backHome: 'Terug naar home',
    },
    home: {
      hero: {
        eyebrow: 'Academy of Squash',
        title: 'Presence Over Performance',
        subtitle:
          'Topniveau squashcoaching vanuit bewustzijn, intentie en meesterschap — door een PSA World Tour-speler met meer dan 15 jaar ervaring.',
        primaryCta: 'Boek een sessie',
        secondaryCta: "Ontdek onze programma's",
        imageAlt: 'Squashspeler slaat een forehand tijdens een wedstrijd',
      },
      sponsors: {
        eyebrow: 'Vertrouwde partners',
        title: 'Het team achter het team',
        description:
          'Materiaal en samenwerkingen die spelers helpen met vertrouwen te trainen en te presteren.',
        fallbackPartnerRole: 'Partnerorganisatie',
        squashpointRole: 'Officiële materiaalpartner',
        missingPartnerNotice:
          'Voeg NEXT_PUBLIC_PORTWISE_URL toe aan je omgeving om een extra partnerkaart te tonen.',
      },
      philosophy: {
        eyebrow: 'Presence Over Performance',
        title: 'Bouw een',
        titleItalic: 'competitief voordeel',
        p1: 'Veel spelers zitten in dezelfde val: ze spelen het spel, maar zijn niet echt in het spel. Ze herhalen de vorige rally, maken zich zorgen over het volgende punt, en die mentale ruis kost meer punten dan techniek ooit zal doen.',
        p2: 'Spelers die zichzelf kennen en onder druk hun emotionele kracht kunnen aanspreken, spelen met een vrijheid die niet te evenaren is. Ze vechten niet tegen wie ze zijn op de baan. Ze komen tot leven.',
        p3: 'Wanneer je echt in het moment bent, volgen resultaten vanzelf. Wij geven je de tools om daar te komen en daar te blijven.',
        cta: 'Bekijk hoe wij aanwezigheid trainen',
        imageAlt: 'Speler in een intense rally terwijl hij kalm blijft op de baan',
      },
      about: {
        eyebrow: 'Over Blessing & de Academy',
        yearsExperience: 'Jaar ervaring',
        title: 'Geboren uit een leven in',
        titleItalic: 'beweging.',
        p1: 'Ik groeide op in Zimbabwe met de droom om ooit op de PSA in Europa te spelen. Die droom bracht me van huis naar de universiteit in Zuid-Afrika, en uiteindelijk naar Nederland.',
        p2: 'Onderweg speelde en coachte ik in verschillende landen en culturen. Elke tegenstander, teamgenoot en speler bracht een verhaal dat mijn kijk op het spel veranderde.',
        p3: 'Wat ik ontdekte: squash gaat nooit alleen over squash. Het gaat over de persoon met het racket. Academy of Squash is op dat inzicht gebouwd.',
        playersCoached: 'Spelers gecoacht',
        continentsPlayed: 'Continenten gespeeld',
        cta: 'Train met Blessing — boek nu',
        imageAlt: 'Hoofdcoach met squashracket op de baan',
      },
      whoWeWorkWith: {
        eyebrow: 'Met wie we werken',
        title: 'Elk niveau. Elk verhaal.',
        titleItalic: 'Eén aanpak.',
        p1: 'Of je nu nog nooit een racket hebt vastgehouden, nationaal speelt, als junior je eerste ranking najaagt, of als corporate team een echte gedeelde uitdaging zoekt: we starten altijd met dezelfde vraag:',
        highlight: 'Wie ben jij, en wat brengt je hier?',
        p2: 'Als je bij ons de baan op stapt, hoef je geen versie van jezelf te spelen waarvan je denkt dat wij die willen zien. Je hoeft alleen te verschijnen; de rest doen we samen.',
        cta: 'Vind het juiste programma',
        imageAlt: 'Groep Academy of Squash-spelers na een training',
      },
      holistic: {
        eyebrow: 'Het complete plaatje',
        title: 'Betere speler.',
        titleItalic: 'Meer gegronde persoon.',
        p1: 'Wij nemen fysieke en mentale prestaties serieus. Onze programma’s zijn opgebouwd met strength & conditioning coaches, fysio’s en experts in mentale performance.',
        p2: 'We doen tactische analyse, videoreview en toernooivoorbereiding. Elk prestatietool ligt op tafel, maar alles staat in een groter kader.',
        p3: 'Het belangrijkste dat we bouwen is een speler die zich bewuster, veerkrachtiger en meer zichzelf voelt op de baan dan bij binnenkomst.',
        cta: 'Boek een intake',
        imageAlt: 'Coach observeert een sessie en ondersteunt de complete speler',
      },
      services: {
        eyebrow: "Onze programma's",
        title: 'Elke service start met',
        titleItalic: 'jouw verhaal',
        intro:
          'We helpen je de emotionele staat te vinden die je beste squash vrijmaakt, en trainen je om daar consistent bij te komen.',
        cards: [
          {
            id: 'service_private',
            icon: 'UserIcon',
            title: 'Privécoaching',
            description:
              'Een-op-een sessies rond jouw verhaal. We verfijnen techniek, ontwikkelen strategie en bouwen mentale gewoontes die passen bij hoe jij natuurlijk speelt.',
            ctaLabel: 'Boek een sessie',
          },
          {
            id: 'service_group',
            icon: 'UserGroupIcon',
            title: 'Groepstraining & clinics',
            description:
              'Energieke sessies waarin je leert jezelf onder druk te uiten via drills, matchplay en samen leren.',
            ctaLabel: 'Reserveer je plek',
          },
          {
            id: 'service_junior',
            icon: 'AcademicCapIcon',
            title: 'Jeugdontwikkeling',
            description:
              'Langetermijnroutes voor jonge sporters die zelfbewustzijn, veerkracht en echte groeimotivatie opbouwen vóór kortetermijnresultaten.',
            ctaLabel: 'Start training',
          },
          {
            id: 'service_tournament',
            icon: 'TrophyIcon',
            title: 'Toernooivoorbereiding',
            description:
              'Wedstrijdvoorbereiding met tactische analyse en routines die je helpen je beste spel te spelen onder hoge druk.',
            ctaLabel: 'Boek een proef',
          },
          {
            id: 'service_fitness',
            icon: 'BoltIcon',
            title: 'Fitness & conditie',
            description:
              'Kracht-, wendbaarheids- en uithoudingsprogramma’s met sportspecialisten zodat lichaam en geest aanwezig blijven in lange rallies.',
            ctaLabel: 'Vraag nu aan',
          },
          {
            id: 'service_video',
            icon: 'VideoCameraIcon',
            title: 'Videoanalyse',
            description:
              'Gedetailleerde beeldanalyse die verborgen patronen in beweging, keuzes en emotie zichtbaar maakt voor gerichte verbetering.',
            ctaLabel: 'Boek analyse',
          },
          {
            id: 'service_corporate',
            icon: 'BriefcaseIcon',
            title: 'Corporate experiences',
            description:
              'Baan-gebaseerde sessies voor leiderschap, communicatie en verbinding met directe vertaling naar de werkvloer.',
            ctaLabel: 'Plan een event',
          },
        ],
      },
      pricing: {
        eyebrow: 'Tarieven',
        title: 'Duidelijke',
        titleItalic: 'startpunten',
        intro:
          'Indicatieve bedragen helpen je opties vergelijken — uiteindelijke tarieven hangen af van locatie, frequentie en programma. Boek online of vraag een offerte op maat aan.',
        fromPrefix: 'Vanaf',
        privateFallback: 'Vraag naar de actuele pakketten',
        groupFallback: 'Seizoensblokken — vraag naar data',
        rows: [
          {
            title: 'Privécoaching',
            note: 'Sessies op maat voor jouw spel; bundels beschikbaar.',
            cta: 'Reserveer je plek',
          },
          {
            title: 'Groep clinics & training',
            note: 'Intensieve blokken met duidelijke progressie.',
            cta: 'Start training',
          },
          {
            title: 'Jeugdontwikkeling',
            note: 'Langetermijnfocus op bewustzijn en liefde voor het spel.',
            cta: 'Bekijk beschikbaarheid',
            hint: 'Routes per leeftijd en ambitie',
          },
        ],
      },
      camps: {
        eyebrow: 'Zomerkampen',
        title: 'Train internationaal.',
        titleItalic: 'Speel overal.',
        intro:
          'Elke zomer combineren we topcoaching met culturele onderdompeling voor een ontwikkelervaring die verder gaat dan een gewone trainingsweek. Materiaal voor geselecteerde programma’s wordt ondersteund door onze partner',
        squashpoint: 'Squashpoint',
        announcement:
          'Data voor zomer 2026 zijn onder voorbehoud — registreer hieronder je interesse en hoor het als eerste.',
        registerInterest: 'Interesse registreren',
        registerAriaPrefix: 'Interesse registreren voor',
        registerAriaSuffix: 'zomerkamp',
        callout:
          'Onze zomerkampen zijn bewust selectief zodat elke speler gerichte coaching krijgt. Vroege aanvragen worden aanbevolen.',
        cards: [
          {
            id: 'camp_train_abroad',
            title: 'Train internationaal',
            subtitle: 'Topcoaching blokken',
            description:
              'Elke zomer gaat de Academy op pad en trainen we in omgevingen op hoog niveau die spelers uitdagen snel te schakelen.',
            image: '/assets/images/aos-camp-train.jpg',
            imageAlt: 'Squashspeler traint op tempo tijdens een internationaal kamp',
          },
          {
            id: 'camp_compete_everywhere',
            title: 'Speel overal',
            subtitle: 'Internationale wedstrijdervaring',
            description:
              'Spelers ontmoeten atleten uit verschillende landen, spelen in nieuwe contexten en ontdekken nieuwe lagen van hun eigen spel.',
            image: '/assets/images/aos-camp-compete.jpg',
            imageAlt: 'Groep spelers tijdens een trainingsdag van een zomerkamp',
          },
          {
            id: 'camp_transformative_growth',
            title: 'Transformatieve groei',
            subtitle: 'Meer dan vakantiekampen',
            description:
              'Deze kampen zijn ontworpen om technische, mentale en persoonlijke groei te versnellen zodat spelers completer terugkomen.',
            image: '/assets/images/aos-camp-grow.jpg',
            imageAlt: 'Coach bespreekt beweging met spelers tijdens een intensieve kampdag',
          },
        ],
      },
      testimonials: {
        eyebrow: 'Stemmen van spelers',
        title: 'Verhalen waar we',
        titleItalic: 'trots op zijn',
        p1: 'We zien elke week betekenisvolle groei op de baan. Uitgebreide testimonials delen we op aanvraag, en we verzamelen toestemming om binnenkort naamgebonden quotes te publiceren.',
        p2: 'Wil je een referentie van een speler of organisatie die bij je past? Vraag ernaar bij je boeking of aanvraag — dan maken we de juiste koppeling.',
        primaryCta: 'Boek een sessie',
        secondaryCta: 'Eerst overleggen',
      },
      contact: {
        eyebrow: 'Klaar om de baan op te stappen?',
        titleBefore: 'Vind jouw',
        titleItalic: 'verhaal.',
        p1: 'Squash is een kleine box. Vier muren, een bal, en nergens om je te verstoppen. In die box is er enorme ruimte om te laten zien wie je bent.',
        p2: 'Wat je ook naar de baan brengt — plezier, ambitie, competitie, of gewoon beter willen worden — wij staan voor je klaar.',
        validEmail: 'Vul een geldig e-mailadres in.',
        submitErrorFallback: 'Er ging iets mis bij het verzenden van je bericht.',
        networkError: 'Netwerkfout. Probeer het zo opnieuw.',
        success: 'Dank je wel. Je aanvraag is verzonden en ontvangen. We reageren binnen 24 uur.',
        fullName: 'Volledige naam *',
        fullNamePlaceholder: 'Jan Jansen',
        emailAddress: 'E-mailadres *',
        emailPlaceholder: 'jan@example.com',
        phoneOptional: 'Telefoon (optioneel)',
        serviceInterest: 'Interesse in service *',
        serviceAria: 'Selecteer de service waarin je geinteresseerd bent',
        message: 'Bericht',
        messagePlaceholder: 'Vertel ons over je doelen, niveau en welke ondersteuning je zoekt.',
        privacyAgreementPrefix: 'Door dit formulier te versturen ga je akkoord met ons',
        privacyPolicy: 'privacybeleid',
        privacyAgreementSuffix: 'We reageren binnen 24 uur.',
        sending: 'Verzenden...',
        sendMessage: 'Verstuur bericht',
        infoEmail: 'E-mail',
        infoPhone: 'Telefoon',
        infoLocation: 'Locatie',
        location: 'Regio Randstad, Nederland',
        services: [
          'Privécoaching',
          'Groepstraining & clinics',
          'Jeugdontwikkeling',
          'Toernooivoorbereiding',
          'Fitness & conditie',
          'Videoanalyse',
          'Zomerkampen',
          'Corporate experiences',
          'Overig',
        ],
      },
    },
    pages: {
      corporate: {
        metadataTitle: 'Corporate squash events',
        metadataDescription:
          'Teambuilding en leiderschapsontwikkeling via squash in de Randstad — op maat gemaakte corporate sessies met Academy of Squash.',
        backHome: '← Terug naar home',
        title: 'Corporate',
        titleItalic: 'squash events',
        intro:
          'Breng je team de baan op met sessies die communicatie, druk en reflectie combineren. We ontwerpen formats voor leadership offsites, afdelingsdagen en klantrelaties — van introducties tot meerdaagse programma’s met begeleide debriefs.',
        bullets: [
          'Indoor squashlocaties in de Randstad (locatie per boeking bevestigd)',
          'Indoor/outdoor warming-ups en teamchallenges waar de faciliteit dit toelaat',
          'Optionele koppeling aan werkvloerthema’s: feedback, beslissen onder druk, vertrouwen',
        ],
        primaryCta: 'Boek of vraag aan',
        secondaryCta: 'Contactformulier',
      },
      coach: {
        metadataTitle: 'Coach & PSA-achtergrond',
        metadataDescription:
          'PSA World Tour-speler met meer dan 15 jaar coachingervaring — het verhaal achter Academy of Squash en Presence Over Performance.',
        backHome: '← Terug naar home',
        title: 'Coach',
        titleItalic: 'bio',
        p1: 'Blessing bouwde Academy of Squash op een eenvoudig idee: squash laat zien wie je bent onder druk. Als speler op de PSA World Tour en coach op meerdere continenten draait het werk om aanwezigheid — in het moment blijven, je emotionele model gebruiken en vrij spelen op beslissende momenten.',
        p2: 'Met 15+ jaar coachingervaring en brede internationale wedstrijdervaring combineren sessies technische helderheid met mentale vaardigheden die verder reiken dan de baan.',
        p3: 'Filosofie: Presence Over Performance — prestatie is het bijproduct van hoe aanwezig, intentioneel en zelfbewust je kunt zijn in de hitte van de wedstrijd.',
        cta: 'Train met Blessing — boek nu',
      },
      blog: {
        metadataTitle: 'Blog & inzichten',
        metadataDescription:
          'Inzichten over squashtraining, beweging en Academy-updates — binnenkort beschikbaar.',
        backHome: '← Terug naar home',
        title: 'Inzichten',
        titleItalic: 'binnenkort',
        body: 'We werken aan artikelen en video’s over beweging, mindset en wedstrijdstrategie. Volg ons intussen op social media of neem contact op voor coaching-updates.',
        cta: 'Houd me op de hoogte',
      },
      privacy: {
        metadataTitle: 'Privacybeleid',
        metadataDescription:
          'Hoe Academy of Squash omgaat met persoonsgegevens, cookies en communicatie voor bezoekers en klanten in Nederland.',
        backHome: '← Terug naar home',
        title: 'Privacybeleid',
        updated: 'Laatst bijgewerkt: 8 april 2026',
        paragraphs: [
          'Academy of Squash (“wij”, “ons”) respecteert je privacy. In dit beleid leggen we uit welke gegevens we verzamelen wanneer je academyofsquash.com gebruikt, hoe we die gebruiken en welke rechten je hebt onder de AVG (GDPR).',
        ],
        sections: [
          {
            title: 'Wie wij zijn',
            body: 'Contact: info@academyofsquash.com\nTelefoon: +31 6 5896 9744\nLocatie: Regio Randstad, Nederland',
          },
          {
            title: 'Gegevens die wij verzamelen',
            list: [
              'Contactformulier: naam, e-mail, telefoon (optioneel), interessegebied en je bericht — om op aanvragen te reageren en coaching te organiseren.',
              'Technische data: standaard server- en analysetoegang (zoals paginabezoeken) als je analytics cookies accepteert.',
            ],
          },
          {
            title: 'Rechtsgronden',
            body: 'We verwerken aanvraaggegevens op basis van overeenkomst of gerechtvaardigd belang om op verzoeken te reageren. Analytics draait alleen op basis van jouw toestemming via de cookiebanner.',
          },
          {
            title: 'Bewaartermijn',
            body: 'We bewaren aanvraaggegevens alleen zolang nodig om je verzoek en eventuele opvolgende coachingrelatie te ondersteunen, tenzij de wet een langere termijn vereist.',
          },
          {
            title: 'Jouw rechten',
            body: 'Je kunt inzage, correctie, verwijdering, beperking of overdraagbaarheid van je persoonsgegevens vragen en bezwaar maken tegen bepaalde verwerkingen. Je kunt ook een klacht indienen bij de Autoriteit Persoonsgegevens.',
          },
          {
            title: 'Cookies',
            body: 'Essentiële cookies zijn nodig om de site te laten werken. Optionele analytics cookies worden alleen geactiveerd als je kiest voor “Analytics accepteren” in de banner.',
          },
        ],
        disclaimer:
          'Deze tekst is een praktische samenvatting en vervangt geen juridisch advies. Laat dit afstemmen met je jurist voor jullie exacte setup (hosting, e-mailprovider, boekingstool).',
      },
      terms: {
        metadataTitle: 'Algemene voorwaarden',
        metadataDescription:
          'Gebruiksvoorwaarden voor de Academy of Squash website en gerelateerde diensten.',
        backHome: '← Terug naar home',
        title: 'Algemene voorwaarden',
        updated: 'Laatst bijgewerkt: 8 april 2026',
        intro:
          'Door deze website te gebruiken ga je akkoord met deze voorwaarden. Coaching, kampen en corporate events worden geboekt onder aparte overeenkomsten of bevestigingen van Academy of Squash.',
        sections: [
          {
            title: 'Gebruik van de site',
            body: 'Inhoud wordt ter informatie aangeboden. We kunnen pagina’s, programma’s en beschikbaarheid zonder voorafgaande melding wijzigen. Externe links (partners, boekingstools) zijn voor gemak; we zijn niet verantwoordelijk voor websites of diensten van derden.',
          },
          {
            title: 'Aansprakelijkheid',
            body: 'Squash is een fysieke sport. Deelnemers zijn verantwoordelijk voor hun eigen gezondheid en het volgen van faciliteitsregels. Voor zover wettelijk toegestaan beperken we aansprakelijkheid uit gebruik van deze website; aansprakelijkheid rond letsel of diensten valt onder je coaching- of eventovereenkomst en toepasselijk recht.',
          },
          {
            title: 'Intellectueel eigendom',
            body: 'Teksten, afbeeldingen en branding op deze site zijn eigendom van Academy of Squash of haar licentiegevers. Hergebruik zonder toestemming is niet toegestaan.',
          },
        ],
        disclaimer: 'Laat deze voorwaarden vóór publicatie beoordelen door je juridisch adviseur.',
      },
    },
  },
} as const;

export type PublicStrings = (typeof publicCopy)[PublicLocale];

export function getPublicStrings(locale: PublicLocale): PublicStrings {
  return publicCopy[locale];
}

export function getRootMetadata(locale: PublicLocale): Metadata {
  const copy = getPublicStrings(locale).metadata;
  const localePath = locale === 'en' ? '/en' : '/nl';

  return {
    metadataBase: new URL(siteUrl),
    title: {
      default: copy.siteTitle,
      template: '%s | Academy of Squash',
    },
    description: copy.siteDescription,
    icons: {
      icon: [{ url: '/favicon.ico', type: 'image/x-icon' }],
    },
    openGraph: {
      type: 'website',
      url: `${siteUrl}${localePath}`,
      siteName: 'Academy of Squash',
      locale: locale === 'en' ? 'en_NL' : 'nl_NL',
      title: copy.siteTitle,
      description: copy.ogDescription,
    },
    twitter: {
      card: 'summary_large_image',
      title: copy.siteTitle,
      description: copy.twitterDescription,
    },
    alternates: {
      canonical: localePath,
      languages: {
        en: '/en',
        'nl-NL': '/nl',
      },
    },
  };
}

export function getLocalizedPageMetadata(
  locale: PublicLocale,
  title: string,
  description: string,
  path: string
): Metadata {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;

  return {
    title,
    description,
    alternates: {
      canonical: `/${locale}${normalizedPath === '/' ? '' : normalizedPath}`,
      languages: {
        en: `/en${normalizedPath === '/' ? '' : normalizedPath}`,
        'nl-NL': `/nl${normalizedPath === '/' ? '' : normalizedPath}`,
      },
    },
  };
}
